// Copyright 2018 Sisense Inc. All rights reserved.

/**
 * @fileoverview A base class for event objects. Implementation based on Google
 * Closure Library goog.events.Event class.
 *
 * @link https://google.github.io/closure-library/api/goog.events.Event.html
 * @author artem.lit@sisense.com (Artem Lytvynov)
 * @copyright sisense@sisense.com (Sisense Ltd.)
 * @license Sisense License
 */

'use strict';

/**
 * A base class for event objects, so that they can support preventDefault
 * and stopPropagation.
 *
 * @suppress {underscore} Several properties on this class are technically
 * public, but referencing these properties outside this package is strongly
 * discouraged.
 */
class Event {
    /**
     * @constructor
     * @param {string} type Event Type.
     * @param {EventTarget=} opt_target Reference to the object that is the
     * target of this event. It has to implement the {@code EventTarget}
     * interface declared at {@link http://developer.mozilla.org/en/DOM/EventTarget}.
     */
    constructor(type, opt_target) {
        /**
         * Event type.
         * @type {string}
         */
        this.type = type;

        /**
         * @type {EventTarget}
         */
        this.target = opt_target;

        /**
         * Object that had the listener attached.
         * @type {EventTarget}
         */
        this.currentTarget = this.target;

        /**
         * Whether to cancel the event in internal capture/bubble processing
         * for old IE.
         * @type {boolean}
         * @public
         */
        this.propagationStopped_ = false;

        /**
         * Whether the default action has been prevented.
         * This is a property to match the W3C specification at
         * {@link http://www.w3.org/TR/DOM-Level-3-Events/#events-event-type-defaultPrevented}.
         * Must be treated as read-only outside the class.
         * @type {boolean}
         */
        this.defaultPrevented = false;

        /**
         * Return value for in internal capture/bubble processing for IE.
         * @type {boolean}
         * @public
         * @suppress {underscore|visibility} Technically public, but
         * referencing this outside this package is strongly discouraged.
         */
        this.returnValue_ = true;
    }

    /**
     * Stops event propagation.
     */
    stopPropagation() {
        this.propagationStopped_ = true;
    }

    /**
     * Prevents the default action, for example a link redirecting to a url.
     */
    preventDefault() {
        this.defaultPrevented = true;
        this.returnValue_ = false;
    }
}

/**
 * Stops the propagation of the event. It is equivalent to
 * {@code e.stopPropagation()}.
 * @param {!Event} e An event.
 */
Event.stopPropagation = function (e) {
    e.stopPropagation();
};

/**
 * Prevents the default action. It is equivalent to
 * {@code e.preventDefault()}.
 * @param {!Event} e An event.
 */
Event.preventDefault = function (e) {
    e.preventDefault();
};

// Exports:
exports.Event = Event;
